import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import FallbackCard from 'src/modules/dashboard/components/FallbackCard';
import { getMyProviders } from 'src/modules/providers/selectors/providersSelectors';
import { NewDailyPauseCard } from 'src/modules/scheduledContent/components/DailyPauseCard';
import CrisisSupportBanner from 'src/shared/components/CrisisSupportBanner';
import {
    ActionItem,
    ActionItemList,
    ActionItemListSkeleton,
    ActionItemSkeleton,
    ActionPropsWithId,
    ContentBannerSkeleton,
    ContentCardsRowSkeleton,
    Grid,
    GridItem,
    layout,
    PageHeader,
    PageHeaderSkeleton,
    SectionHeader,
    SectionHeaderSkeleton,
} from 'src/shared/ds';
import { useLocalizedVersion } from 'src/shared/hooks/useLocalizedVersion';

import { captureException } from '@sentry/browser';
import {
    HorizontalContentCard,
    VerticalContentCard,
} from 'src/modules/dashboard/components/ContentCard';
import { ForYouModal } from 'src/modules/dashboard/components/ForYouModal';
import { pxToRem } from 'src/shared/helpers/pxToRem';
import { useGetHomeScreenQuery } from '../documents/getHomeScreen.generated';
import { mockHomeScreenClient } from '../mock/graphql';
import { ContentCollectionRenderComponent } from 'src/generated/webapp_gql';
import { mapContentLinkToSectionHeaderProps } from 'src/modules/dashboard/helpers/contentLinkNodeToSectionHeader';

const Wrapper = styled.div({
    width: '100%',
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    paddingLeft: layout.containerPaddingX,
    paddingRight: layout.containerPaddingX,
    paddingBottom: layout.footerHeight,
});

// TODO: Should be renamed to HomePage once home screen revamp phase 1 is fully rolled out
export const NewHomePage: React.FC = () => {
    const { t } = useTranslation();
    const user = useSelector(getUserInformation);
    const myProviders = useSelector(getMyProviders);
    const [localizedVersion] = useLocalizedVersion();
    const { showDailyPauseOnWeb } = useFlags();
    const [isForYouModalVisible, setIsForYouModalVisible] = useState(false);

    const { data, loading: homeScreenLoading, error } = useGetHomeScreenQuery({
        // TODO(evan-mh): Remove once our primary client hosts the home screen API
        client: mockHomeScreenClient,
    });

    useEffect(() => {
        if (error) {
            captureException(error);
        }
    }, [error]);

    if (homeScreenLoading) {
        return (
            <Wrapper>
                <PageHeaderSkeleton />
                <Grid>
                    <GridItem lg={3} md={2}>
                        <ContentBannerSkeleton />
                    </GridItem>
                    <GridItem lg={2} md={2}>
                        <ActionItemListSkeleton actions={[{}, {}, {}]} showSecondaryButton />
                    </GridItem>
                </Grid>
                <SectionHeaderSkeleton showSecondaryButton={true} />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
                <SectionHeaderSkeleton showSecondaryButton={true} />
                <Grid lgMode="3columns">
                    <GridItem lg={1} md={2}>
                        <ActionItemListSkeleton actions={[{}, {}, {}]} showSecondaryButton />
                    </GridItem>
                    <GridItem lg={1} md={2} hide="sm">
                        <ActionItemListSkeleton actions={[{}, {}, {}]} showSecondaryButton />
                    </GridItem>
                    <GridItem lg={1} md={2} hide="md">
                        <ActionItemListSkeleton actions={[{}, {}, {}]} showSecondaryButton />
                    </GridItem>
                </Grid>
                <SectionHeaderSkeleton showSecondaryButton={false} />
                <Grid lgMode="3columns">
                    <GridItem lg={1} md={2}>
                        <ActionItemSkeleton hideIcon={true} hideDescription={true} />
                    </GridItem>
                </Grid>
            </Wrapper>
        );
    }

    // TODO(evan-mh): Handle data load failure.
    if (!data) {
        return <div>Failed to load: {error?.message}</div>;
    }

    const { homeScreen } = data;

    if (!homeScreen) {
        return <div>Failed to load, present fallback.</div>;
    }

    // TODO: This will be non-null.
    const sections = homeScreen.screenSections;
    const scheduledContent = homeScreen.scheduledContent;

    const forYouActions: ActionPropsWithId[] = [
        {
            id: '1',
            onClick: () => null,
            title: 'Select your care provider',
            description: 'Get started by selecting a professional',
            startIcon: 'temporary-for-you-image-1',
            startIconBackgroundColor: 'blue800',
        },
        {
            id: '2',
            onClick: () => null,
            title: 'View your care form',
            description: 'You completed it on 11/15/2020',
            startIcon: 'temporary-for-you-image-2',
            startIconBackgroundColor: 'blue800',
        },
        {
            id: '3',
            onClick: () => null,
            title: 'Practice mindfulness',
            description: 'Browse our library of resources',
            startIcon: 'temporary-for-you-image-3',
            startIconBackgroundColor: 'yellow800',
        },
    ];

    return (
        <Wrapper>
            <PageHeader>Hello, {user.firstName} 👋</PageHeader>
            <Grid>
                <GridItem lg={3} md={2}>
                    {!showDailyPauseOnWeb || localizedVersion || !scheduledContent ? (
                        <FallbackCard providers={myProviders} />
                    ) : (
                        <NewDailyPauseCard scheduledContent={scheduledContent} />
                    )}
                </GridItem>
                <GridItem lg={2} md={2}>
                    {/*"For you" (or content in its place) will appear as featuredCollection from the production API.*/}
                    <ActionItemList
                        title="For you"
                        secondaryText={t('shared:view_all')}
                        onSecondaryTextPress={() => setIsForYouModalVisible(true)}
                        actions={forYouActions}
                    />
                </GridItem>
            </Grid>
            {sections.map(section => {
                if (!section) return null;

                return (
                    <React.Fragment key={section.id}>
                        <SectionHeader
                            title={section.title}
                            {...mapContentLinkToSectionHeaderProps(section.ctaLink)}
                        />

                        {section.renderComponent ===
                            ContentCollectionRenderComponent.Horizontal && (
                            <HorizontalContentCard links={section.contentCollectionLinks} />
                        )}
                        {section.renderComponent === ContentCollectionRenderComponent.Vertical && (
                            <VerticalContentCard links={section.contentCollectionLinks} />
                        )}
                    </React.Fragment>
                );
            })}
            <SectionHeader title={'Your care'} />
            <Grid lgMode="3columns">
                <GridItem lg={1} md={2} column>
                    <ActionItem
                        onClick={() => null}
                        title={'Check your wellbeing'}
                        startIconBackgroundColor="#F79EFF"
                        startIcon="temporary-for-you-image-3"
                    />
                    <CrisisContainer>
                        <CrisisSupportBanner />
                    </CrisisContainer>
                </GridItem>
            </Grid>
            {isForYouModalVisible && (
                <ForYouModal
                    handleClose={() => setIsForYouModalVisible(false)}
                    forYouActions={forYouActions}
                />
            )}
        </Wrapper>
    );
};

const CrisisContainer = styled.div({
    marginTop: pxToRem(20),
});
